import * as React from "react";
import {
  CircleUserRound,
  Command as CommandIcon,
  Coffee,
  FileText,
  MessageSquare,
  Package,
  Rocket,
  Search,
  Truck,
  X as XIcon,
} from "lucide-react";
import { useQuery } from "convex/react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/convexApi";
import { ENV } from "@/env";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { getNavGroupsForRole, type NavItem } from "@/components/layout/nav.config";
import { hasPermission } from "@/lib/roles";
import { parseInternalSearchInput, splitSearchTokens } from "@/lib/internal-search-query";
import {
  resolveInternalSearchDestination,
  resolveInternalUsersDestination,
} from "@/lib/internal-search-routing";
import { getEnterpriseBadgeClass, getEnterpriseBadgeLabel } from "@/lib/customer-tag-colors";
import {
  getContractStatusBadgeClass,
  getInventoryStatusBadgeClass,
  getOrderStatusBadgeClass,
} from "@/lib/status-tag-colors";
import { pickAssortedBadgeClass, BADGE_TONES } from "@/lib/badge-tones";
import { formatStatusLabel } from "@/lib/format";
import type {
  InternalSearchResponse,
  ScopeToken,
  SearchEntity,
  SearchHit,
} from "@/types/internal-search";

type PaletteAction = {
  id: string;
  group: string;
  label: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  keywords?: string[];
};

const ENTITY_GROUP_LABEL: Record<SearchEntity, string> = {
  user: "Users",
  order: "Orders",
  contract: "Contracts",
  cargo: "Cargos",
  product: "Products",
  thread: "Threads",
};

const ENTITY_ICON: Record<SearchEntity, React.ComponentType<{ className?: string }>> = {
  user: CircleUserRound,
  order: Truck,
  contract: FileText,
  cargo: Package,
  product: Coffee,
  thread: MessageSquare,
};

const SCOPE_BANDS: Array<{
  scope: SearchEntity;
  token: string;
  label: string;
  hint: string;
  aliases: string[];
}> = [
  { scope: "user", token: "u", label: "Users", hint: "Search users", aliases: ["u", "user", "users"] },
  { scope: "order", token: "o", label: "Orders", hint: "Search orders", aliases: ["o", "order", "orders"] },
  { scope: "contract", token: "ct", label: "Contracts", hint: "Search contracts", aliases: ["ct", "contract", "contracts"] },
  { scope: "cargo", token: "cg", label: "Cargos", hint: "Search cargos", aliases: ["cg", "cargo", "cargos"] },
  { scope: "product", token: "p", label: "Products", hint: "Search products", aliases: ["p", "product", "products"] },
  { scope: "thread", token: "t", label: "Threads", hint: "Search support threads", aliases: ["t", "th", "thread", "threads"] },
];

const SCOPE_ALIAS_TO_TOKEN = SCOPE_BANDS.reduce<Record<string, ScopeToken>>(
  (acc, band) => {
    for (const alias of band.aliases) acc[alias] = band.scope;
    return acc;
  },
  { all: "all", a: "all" },
);

function resolveScopeAlias(token: string): ScopeToken | null {
  const normalized = token.trim().toLowerCase();
  if (!normalized) return null;
  return SCOPE_ALIAS_TO_TOKEN[normalized] ?? null;
}

function flattenNavItems(groupLabel: string, items: NavItem[]): PaletteAction[] {
  return items.flatMap((item) => {
    const parent: PaletteAction = {
      id: `${groupLabel}-${item.path}`,
      group: groupLabel,
      label: item.label,
      path: item.path,
      icon: CommandIcon,
      keywords: [groupLabel.toLowerCase(), item.label.toLowerCase()],
    };

    const children = (item.children ?? []).map((child) => ({
      id: `${groupLabel}-${child.path}`,
      group: groupLabel,
      label: `${item.label} - ${child.label}`,
      path: child.path,
      icon: CommandIcon,
      keywords: [
        groupLabel.toLowerCase(),
        item.label.toLowerCase(),
        child.label.toLowerCase(),
      ],
    }));

    return [parent, ...children];
  });
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function HighlightedText({ text, query }: { text: string; query: string }) {
  const tokens = React.useMemo(() => splitSearchTokens(query), [query]);

  if (!text || tokens.length === 0) return <>{text}</>;

  const pattern = tokens
    .map(escapeRegex)
    .sort((a, b) => b.length - a.length)
    .join("|");

  if (!pattern) return <>{text}</>;

  const parts = text.split(new RegExp(`(${pattern})`, "ig"));
  const lowerTokens = new Set(tokens);

  return (
    <>
      {parts.map((part, idx) => {
        const isMatch = lowerTokens.has(part.toLowerCase());
        return isMatch ? (
          <mark key={`${part}-${idx}`} className="rounded-sm bg-amber-200/70 px-0.5 text-current dark:bg-amber-500/30">
            {part}
          </mark>
        ) : (
          <React.Fragment key={`${part}-${idx}`}>{part}</React.Fragment>
        );
      })}
    </>
  );
}

function toDisplayHits(
  hits: SearchHit[],
  userRole: string | undefined,
): Array<{ hit: SearchHit; destination: string }> {
  return hits
    .map((hit) => {
      const destination = resolveInternalSearchDestination(hit, userRole);
      return destination ? { hit, destination } : null;
    })
    .filter((item): item is { hit: SearchHit; destination: string } => Boolean(item));
}

type ResultTag = {
  key: string;
  label: string;
  className: string;
};

function getSearchResultTags(hit: SearchHit): ResultTag[] {
  const tags: ResultTag[] = [];

  if (typeof hit.tier === "number" && Number.isFinite(hit.tier) && hit.tier > 0) {
    tags.push({
      key: "tier",
      label: `Tier ${hit.tier}`,
      className: BADGE_TONES.indigo,
    });
  }

  if (hit.isEnterprise === true) {
    tags.push({
      key: "enterprise",
      label: getEnterpriseBadgeLabel(hit.isEnterprise),
      className: getEnterpriseBadgeClass(hit.isEnterprise),
    });
  }

  if (hit.status) {
    const statusClass =
      hit.entity === "order"
        ? getOrderStatusBadgeClass(hit.status)
        : hit.entity === "contract"
          ? getContractStatusBadgeClass(hit.status)
          : hit.entity === "cargo"
            ? getInventoryStatusBadgeClass(hit.status)
          : hit.entity === "thread"
              ? hit.status === "waiting_on_internal"
                ? BADGE_TONES.red
                : hit.status === "waiting_on_customer"
                  ? BADGE_TONES.sky
                  : hit.status === "open"
                    ? BADGE_TONES.sky
                    : hit.status === "resolved"
                      ? BADGE_TONES.emerald
                      : BADGE_TONES.slate
              : BADGE_TONES.slate;

    tags.push({
      key: "status",
      label: formatStatusLabel(hit.status),
      className: statusClass,
    });
  }

  // Keep tags high-signal in list views:
  // show custom profile tags only for user results.
  if (hit.entity === "user") {
    const baseEntityLabels = new Set([
      "user",
      "order",
      "contract",
      "cargo",
      "product",
      "thread",
    ]);
    const hiddenDisplayTags = new Set([
      "customer",
      "standard",
      "enterprise",
      "admin",
      "sales",
      "logistics",
      "inventory",
      "accounting",
      "marketing",
    ]);

    const customTags = [
      ...(hit.userTags ?? []),
      ...(hit.tags ?? []),
    ]
      .map((tag) => String(tag ?? "").trim())
      .filter(
        (tag) =>
          tag.length > 0 &&
          !baseEntityLabels.has(tag.toLowerCase()) &&
          !hiddenDisplayTags.has(tag.toLowerCase()) &&
          (!hit.status || tag.toLowerCase() !== hit.status.toLowerCase()),
      );

    const deduped = [...new Set(customTags)].slice(0, 1);
    for (const tag of deduped) {
      tags.push({
        key: `tag-${tag.toLowerCase()}`,
        label: tag,
        className: pickAssortedBadgeClass(tag),
      });
    }
  }

  return tags;
}

function SearchResultItem({
  hit,
  destination,
  query,
  onSelect,
}: {
  hit: SearchHit;
  destination: string;
  query: string;
  onSelect: (destination: string) => void;
}) {
  const Icon = ENTITY_ICON[hit.entity];
  const resultTags = getSearchResultTags(hit);

  return (
    <CommandItem
      key={`${hit.entity}-${hit.id}`}
      value={`${hit.title} ${hit.companyName ?? ""} ${hit.subtitle ?? ""}`}
      keywords={hit.tags}
      className="items-start py-2"
      onSelect={() => onSelect(destination)}
    >
      <Icon />
      <div className="min-w-0 flex-1 py-0.5">
        <div className="min-w-0 flex-1 truncate pr-2 font-medium">
          <HighlightedText text={hit.title} query={query} />
        </div>
        {hit.companyName &&
        hit.companyName.trim().toLowerCase() !== hit.title.trim().toLowerCase() ? (
          <div className="truncate text-xs text-muted-foreground">
            <HighlightedText text={hit.companyName} query={query} />
          </div>
        ) : null}
        {hit.subtitle ? (
          <div className="truncate text-xs text-muted-foreground">
            <HighlightedText text={hit.subtitle} query={query} />
          </div>
        ) : null}
        {resultTags.length > 0 ? (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {resultTags.map((tag) => (
              <Badge
                key={`${hit.id}-${tag.key}`}
                variant="outline"
                className={`${tag.className} whitespace-nowrap`}
              >
                {tag.label}
              </Badge>
            ))}
          </div>
        ) : null}
      </div>
    </CommandItem>
  );
}

export function InternalGlobalCommandPalette() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userRole, authUserId } = useAuthIdentity();

  const isSearchV2Enabled = ENV.INTERNAL_SEARCH_V2;

  const [open, setOpen] = React.useState(false);
  const [activeScope, setActiveScope] = React.useState<ScopeToken>("all");
  const [scopeCommand, setScopeCommand] = React.useState("");
  const [query, setQuery] = React.useState("");
  const [debouncedQuery, setDebouncedQuery] = React.useState("");
  const inputValue = scopeCommand || query;
  const activeScopeBand = SCOPE_BANDS.find((band) => band.scope === activeScope);

  React.useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedQuery(query);
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [query]);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isTypingTarget =
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.isContentEditable;

      if (isTypingTarget) return;

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((prev) => !prev);
      }
    };

    const onOpenRequest = () => setOpen(true);

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("global-command:open", onOpenRequest);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("global-command:open", onOpenRequest);
    };
  }, []);

  const parsedInput = React.useMemo(
    () => parseInternalSearchInput(inputValue, activeScope),
    [inputValue, activeScope],
  );

  const parsedDebounced = React.useMemo(
    () => parseInternalSearchInput(debouncedQuery, activeScope),
    [debouncedQuery, activeScope],
  );

  const handleInputChange = React.useCallback((nextValue: string) => {
    const normalized = nextValue.trimStart();

    if (normalized.startsWith("!")) {
      const resolvedMatch = normalized.match(/^!([a-z]+)(?:\s*:\s*|\s+)(.*)$/i);
      if (resolvedMatch) {
        const mappedScope = resolveScopeAlias(resolvedMatch[1] ?? "");
        if (mappedScope && mappedScope !== "all") {
          setActiveScope(mappedScope);
          setScopeCommand("");
          setQuery(resolvedMatch[2] ?? "");
          return;
        }
      }

      setScopeCommand(normalized);
      setQuery("");
      setActiveScope("all");
      return;
    }

    setScopeCommand("");
    setQuery(nextValue);
  }, []);

  const shouldRunEntitySearch =
    isSearchV2Enabled &&
    Boolean(authUserId) &&
    scopeCommand.trim().length === 0 &&
    parsedDebounced.normalizedQuery.length >= 2;

  const searchResponse = useQuery(
    (api as any).internalSearch.searchInternalGlobal,
    shouldRunEntitySearch
      ? {
          authUserId,
          query: parsedDebounced.query,
          scope: parsedDebounced.scope,
          limitPerEntity: 8,
        }
      : "skip",
  ) as InternalSearchResponse | undefined;

  const navActions = React.useMemo(() => {
    return getNavGroupsForRole(userRole).flatMap((group) =>
      flattenNavItems(group.label, group.items),
    );
  }, [userRole]);

  const quickActions = React.useMemo<PaletteAction[]>(() => {
    const actions: PaletteAction[] = [];
    if (hasPermission(userRole, "internal:access")) {
      actions.push({
        id: "quick-changelog",
        group: "Quick Links",
        label: "What’s new",
        path: "/changelog",
        icon: Rocket,
        keywords: ["changelog", "features", "releases", "updates"],
      });
    }

    if (hasPermission(userRole, "inventory:view")) {
      actions.push({
        id: "quick-products",
        group: "Quick Links",
        label: "Products",
        path: "/inventory/products",
        icon: Coffee,
        keywords: ["inventory", "products", "catalog"],
      });
      actions.push({
        id: "quick-cargos",
        group: "Quick Links",
        label: "Cargos",
        path: "/inventory/cargos",
        icon: Package,
        keywords: ["inventory", "cargo", "oci"],
      });
    }

    if (hasPermission(userRole, "orders:view")) {
      actions.push({
        id: "quick-orders",
        group: "Quick Links",
        label: "Orders",
        path: "/logistics/orders",
        icon: Truck,
        keywords: ["orders", "shipping", "logistics"],
      });
    }

    const usersPath = resolveInternalUsersDestination(userRole);
    if (usersPath) {
      actions.push({
        id: "quick-users",
        group: "Quick Links",
        label: "Users",
        path: usersPath,
        icon: CircleUserRound,
        keywords: ["users", "customers", "accounts"],
      });
    }

    if (hasPermission(userRole, "contracts:view")) {
      actions.push({
        id: "quick-contracts",
        group: "Quick Links",
        label: "Contracts",
        path: "/sales/contracts",
        icon: FileText,
        keywords: ["contracts", "sales"],
      });
    }

    if (hasPermission(userRole, "internal:access")) {
      actions.push({
        id: "quick-support",
        group: "Quick Links",
        label: "Support threads",
        path: "/support",
        icon: MessageSquare,
        keywords: ["support", "threads", "inbox"],
      });
    }

    return actions;
  }, [userRole]);

  const staticActions = React.useMemo(
    () => [...quickActions, ...navActions],
    [quickActions, navActions],
  );

  const filteredStaticActions = React.useMemo(() => {
    if (parsedInput.scope !== "all") return [] as PaletteAction[];
    if (!parsedInput.normalizedQuery) return staticActions;

    return staticActions.filter((action) => {
      const haystack = `${action.label} ${(action.keywords ?? []).join(" ")}`.toLowerCase();
      return haystack.includes(parsedInput.normalizedQuery);
    });
  }, [parsedInput.normalizedQuery, parsedInput.scope, staticActions]);

  const staticActionsByGroup = React.useMemo(() => {
    const grouped = new Map<string, PaletteAction[]>();
    for (const action of filteredStaticActions) {
      const existing = grouped.get(action.group) ?? [];
      existing.push(action);
      grouped.set(action.group, existing);
    }
    return grouped;
  }, [filteredStaticActions]);

  const filteredScopeBands = React.useMemo(() => {
    const normalized = scopeCommand.trim();
    if (!normalized.startsWith("!")) return [] as typeof SCOPE_BANDS;
    const scopeToken = normalized.match(/^!([a-z]*)$/i)?.[1]?.toLowerCase() ?? "";
    return SCOPE_BANDS.filter((band) =>
      scopeToken ? band.aliases.some((alias) => alias.startsWith(scopeToken)) : true,
    );
  }, [scopeCommand]);

  const topEntityResults = React.useMemo(
    () => toDisplayHits(searchResponse?.top ?? [], userRole),
    [searchResponse?.top, userRole],
  );

  const groupEntityResults = React.useMemo(() => {
    const topKey = new Set(topEntityResults.map(({ hit }) => `${hit.entity}:${hit.id}`));
    const source = searchResponse?.groups;
    if (!source) {
      return {
        users: [],
        orders: [],
        contracts: [],
        cargos: [],
        products: [],
        threads: [],
      };
    }

    const stripTop = (hits: SearchHit[]) =>
      toDisplayHits(
        hits.filter((hit) => !topKey.has(`${hit.entity}:${hit.id}`)),
        userRole,
      );

    return {
      users: stripTop(source.users),
      orders: stripTop(source.orders),
      contracts: stripTop(source.contracts),
      cargos: stripTop(source.cargos),
      products: stripTop(source.products),
      threads: stripTop(source.threads),
    };
  }, [searchResponse?.groups, topEntityResults, userRole]);

  const isSearching = shouldRunEntitySearch && searchResponse === undefined;

  const hasEntityResults =
    topEntityResults.length > 0 ||
    groupEntityResults.users.length > 0 ||
    groupEntityResults.orders.length > 0 ||
    groupEntityResults.contracts.length > 0 ||
    groupEntityResults.cargos.length > 0 ||
    groupEntityResults.products.length > 0 ||
    groupEntityResults.threads.length > 0;

  const hasRenderableContent =
    hasEntityResults ||
    filteredStaticActions.length > 0 ||
    filteredScopeBands.length > 0 ||
    isSearching;

  const handleNavigate = (path: string) => {
    setOpen(false);
    setActiveScope("all");
    setScopeCommand("");
    setQuery("");
    if (`${location.pathname}${location.search}` === path) return;
    navigate(path);
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="hidden min-w-52 justify-between text-muted-foreground md:inline-flex"
      >
        <span className="inline-flex items-center gap-2">
          <Search className="size-4" />
          Go to
        </span>
        <span className="text-xs">⌘K</span>
      </Button>

      <CommandDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            setActiveScope("all");
            setScopeCommand("");
            setQuery("");
          }
        }}
        shouldFilter={false}
        className="sm:max-w-[30rem]"
        title="Internal Command Palette"
        description="Jump to pages and find users, orders, contracts, cargos, products, support threads, and actions."
      >
        <CommandInput
          value={inputValue}
          onValueChange={handleInputChange}
          onKeyDown={(event) => {
            if (
              event.key === "Backspace" &&
              scopeCommand.trim().length === 0 &&
              activeScope !== "all" &&
              query.length === 0
            ) {
              event.preventDefault();
              setActiveScope("all");
            }
          }}
          leftSlot={
            activeScope !== "all" && !scopeCommand ? (
              <span className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-1.5 py-0.5 text-xs font-medium text-foreground">
                <span>{activeScopeBand?.label ?? activeScope}</span>
                <button
                  type="button"
                  className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm text-muted-foreground hover:bg-background hover:text-foreground"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setActiveScope("all");
                  }}
                  aria-label="Clear scope"
                >
                  <XIcon className="size-3" />
                </button>
              </span>
            ) : null
          }
          placeholder={
            activeScope !== "all"
              ? `Search ${activeScopeBand?.label.toLowerCase() ?? activeScope}...`
              : "Try: !u jacob, !o 4436, user:acme, cargo:OCI"
          }
        />
        <CommandList>
          {!hasRenderableContent ? <CommandEmpty>No matching results.</CommandEmpty> : null}

          {isSearching ? (
            <CommandGroup heading="Search">
              <CommandItem disabled>
                <Search className="animate-pulse" />
                <span>Searching internal records...</span>
              </CommandItem>
            </CommandGroup>
          ) : null}

          {filteredScopeBands.length > 0 ? (
            <>
              <CommandGroup heading="Scopes">
                {filteredScopeBands.map((band) => {
                  const Icon = ENTITY_ICON[band.scope];
                  return (
                    <CommandItem
                      key={`scope-${band.scope}`}
                      value={`${band.label} ${band.hint}`}
                      keywords={band.aliases}
                      onSelect={() => {
                        setActiveScope(band.scope);
                        setScopeCommand("");
                        setQuery("");
                      }}
                    >
                      <Icon />
                      <span>{band.hint}</span>
                      <CommandShortcut>{`!${band.token}`}</CommandShortcut>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              <CommandSeparator />
            </>
          ) : null}

          {topEntityResults.length > 0 ? (
            <>
              <CommandGroup heading="Top Results">
                {topEntityResults.map(({ hit, destination }) => {
                  return (
                    <SearchResultItem
                      key={`top-${hit.entity}-${hit.id}`}
                      hit={hit}
                      destination={destination}
                      query={parsedInput.normalizedQuery}
                      onSelect={handleNavigate}
                    />
                  );
                })}
              </CommandGroup>
              <CommandSeparator />
            </>
          ) : null}

          {(
            [
              { key: "users", entity: "user" },
              { key: "orders", entity: "order" },
              { key: "contracts", entity: "contract" },
              { key: "cargos", entity: "cargo" },
              { key: "products", entity: "product" },
              { key: "threads", entity: "thread" },
            ] as const
          ).map(({ key, entity }) => {
            const results = groupEntityResults[key];
            if (results.length === 0) return null;

            return (
              <React.Fragment key={key}>
                <CommandGroup heading={ENTITY_GROUP_LABEL[entity]}>
                  {results.map(({ hit, destination }) => (
                    <SearchResultItem
                      key={`${entity}-${hit.id}`}
                      hit={hit}
                      destination={destination}
                      query={parsedInput.normalizedQuery}
                      onSelect={handleNavigate}
                    />
                  ))}
                </CommandGroup>
                <CommandSeparator />
              </React.Fragment>
            );
          })}

          {[...staticActionsByGroup.entries()].map(([group, actions]) => (
            <React.Fragment key={group}>
              <CommandGroup heading={group}>
                {actions.map((action) => (
                  <CommandItem
                    key={action.id}
                    value={action.label}
                    keywords={action.keywords}
                    onSelect={() => handleNavigate(action.path)}
                  >
                    <action.icon />
                    <span>{action.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
            </React.Fragment>
          ))}

          {searchResponse?.meta.truncated ? (
            <CommandGroup heading="Info">
              <CommandItem disabled>
                <CommandIcon />
                <span>Showing top matches. Keep typing to refine.</span>
                <CommandShortcut>max</CommandShortcut>
              </CommandItem>
            </CommandGroup>
          ) : null}

          {!isSearchV2Enabled ? (
            <CommandGroup heading="Info">
              <CommandItem disabled>
                <CommandIcon />
                <span>Deep entity search is disabled by feature flag.</span>
              </CommandItem>
            </CommandGroup>
          ) : null}
        </CommandList>
      </CommandDialog>
    </>
  );
}

export default InternalGlobalCommandPalette;
